import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { Analisis, Capa, Operacion } from '@laser/core';
import { api } from '../api/cliente';
import { ETIQUETA_OPERACION, OPERACIONES } from '../lib/estados';
import { numero } from '../lib/formato';
import { rutaPoligono } from '../lib/nesting';
import { COLOR_OPERACION, recolorearSvg } from '../lib/svgCapas';

export function LeyendaOperaciones() {
  return (
    <div className="leyenda" aria-label="Colores por operación">
      {OPERACIONES.map((op) => (
        <span key={op}>
          <i style={{ borderTopColor: COLOR_OPERACION[op], borderTopStyle: op === 'ignorar' ? 'dashed' : 'solid' }} />
          {ETIQUETA_OPERACION[op]}
        </span>
      ))}
    </div>
  );
}

/** Dibujo de respaldo con las polilíneas del análisis, por si el SVG no carga. */
function DesdeCapas({ analisis, operaciones }: { analisis: Analisis; operaciones: Record<string, Operacion> }) {
  return (
    <svg viewBox={`0 0 ${analisis.anchoMm} ${analisis.altoMm}`} role="img" aria-label={`Vista de ${analisis.nombreOriginal}`}>
      {analisis.capas.map((c) => {
        const op = operaciones[c.id] ?? c.operacionSugerida;
        const d = c.polilineas.map((p) => rutaPoligono(p.puntos, p.cerrada)).join(' ');
        const relleno = c.tipo === 'relleno' && op === 'grabado';
        return (
          <path key={c.id} d={d} fillRule="evenodd" vectorEffect="non-scaling-stroke"
            fill={relleno ? COLOR_OPERACION.grabado : 'none'} fillOpacity={relleno ? 0.85 : undefined}
            stroke={relleno ? 'none' : COLOR_OPERACION[op]} strokeWidth={1.2} strokeDasharray={op === 'ignorar' ? '4 3' : undefined} />
        );
      })}
    </svg>
  );
}

/**
 * Vista 2D del archivo. `analisis` ya viene escalado (así el dibujo de respaldo y las
 * medidas salen bien); `escalaX/escalaY` son esas mismas escalas, para estirar el SVG
 * normalizado que manda el servidor.
 */
export function Vista2D({ analisis, operaciones, grabarRasters = true, resaltarCapa, escalaX = 1, escalaY = 1 }: {
  analisis: Analisis; operaciones: Record<string, Operacion>; grabarRasters?: boolean; resaltarCapa?: string | null;
  escalaX?: number; escalaY?: number;
}) {
  const svg = useQuery({ queryKey: ['svg', analisis.svgNormalizadoUrl], queryFn: () => api.texto(analisis.svgNormalizadoUrl), staleTime: Infinity, retry: 1 });
  const html = useMemo(() => {
    if (!svg.data) return null;
    try { return recolorearSvg(svg.data, analisis.capas, operaciones, { grabarRasters, resaltarCapa, escalaX, escalaY }); } catch { return null; }
  }, [svg.data, analisis.capas, operaciones, grabarRasters, resaltarCapa, escalaX, escalaY]);

  return (
    <div>
      {html
        ? <div className="vista-2d" role="img" aria-label={`Vista 2D de ${analisis.nombreOriginal}`} dangerouslySetInnerHTML={{ __html: html }} />
        : svg.isLoading
          ? <div className="vista-2d"><span className="vista-2d__estado">Cargando vista…</span></div>
          : <div className="vista-2d" title="No se pudo cargar el SVG normalizado; se dibuja desde el análisis"><DesdeCapas analisis={analisis} operaciones={operaciones} /></div>}
      <LeyendaOperaciones />
    </div>
  );
}

export function resumenCapa(c: Capa): string {
  if (c.tipo === 'relleno') return `${numero(c.areaMm2 / 100)} cm² · ${c.numRutas} rutas`;
  return `${numero(c.longitudMm / 1000)} m · ${c.numRutas} rutas`;
}
